/**
 * Fire incidents and warnings from the states' own feeds (docs/specs/map.md §29). bushfire.io's tiles are not used:
 * its terms allow personal, non-commercial use only without an agreement (§19.6, letter
 * `docs/reference/letters/bushfire-io.md`). Instead Teslyn Field reads the agencies' designated public feeds:
 *
 * - **WA, DFES (Emergency WA)**: the CAP-AU warnings (`/v1/capau`: alert level, warning area polygons) and the RSS
 *   incidents (`/v1/rss/incidents`: points). CC BY-ND 4.0; Emergency WA's terms allow automated access to the
 *   designated feeds only, "limited to one request per data feed every 5 minutes", for their "intended emergency
 *   management purpose". Shown unaltered, with the attribution DFES asks for.
 * - **NSW, RFS**: major incidents GeoJSON (points and fire grounds). CC BY 4.0 with the RFS's attribution.
 * - **Queensland, QFD**: current bushfire incidents and warnings (points and warning areas). CC BY 4.0.
 *
 * SA (CC BY-NC-ND), Victoria (feed access by request) and NT (no live feed found) are not read. Each feed is asked
 * only when the map looks at its state. The agencies' words are shown as published: the alert level, title, status
 * and description are never reworded; only the colour is Teslyn's (the Australian Warning System's colours).
 *
 * Feeds are parsed here (text only: no markup from a feed ever reaches the map); Teslyn Field's Rust core asks the
 * fixed addresses (`fires.rs`).
 */
import type { LatLng } from './planning.js';
import type { GeoBounds } from './map-geo.js';
import type { FeatureStyle, LegendEntry, MapFeature } from './map-layers.js';
import type { IdentifyRow } from './map-kml.js';

export type FireFeedId = 'wa-warnings' | 'wa-incidents' | 'nsw' | 'qld';
export type FireState = 'WA' | 'NSW' | 'QLD';

export interface FireFeedDef {
  readonly id: FireFeedId;
  readonly state: FireState;
  readonly name: string;
  /** The fixed address Teslyn Field asks (the Rust core holds the same list). */
  readonly url: string;
  /** The least time between two requests from a laptop. */
  readonly everyMs: number;
  /** The credit shown while its items are on the map, in the agency's own words. */
  readonly credit: string;
  readonly licence: string;
}

const MIN = 60_000;

/** Emergency WA's attribution (its Copyright section) with the licence. */
export const WA_FIRE_CREDIT =
  '© State of Western Australia acting through the Department of Fire and Emergency Services. For current information go to www.emergency.wa.gov.au. (CC BY-ND 4.0)';
/** The RFS's attribution for its feeds (its feeds page). */
export const NSW_FIRE_CREDIT =
  '© State of New South Wales (NSW Rural Fire Service). For current information go to www.rfs.nsw.gov.au. (CC BY 4.0)';
/** QFD publishes the feed on Open Data Queensland under CC BY 4.0 without its own wording. */
export const QLD_FIRE_CREDIT =
  'Bushfire incidents and warnings © State of Queensland (Queensland Fire Department), CC BY 4.0. For current information go to www.fire.qld.gov.au.';

/**
 * The notice on the fire layer and in every fire popup (decision 796; the research note of 27 Oct 2026 §2.6): the
 * map shows the agencies' information, it is not a warning service.
 */
export const FIRE_NOTICE =
  'Information only, not a safety system. Follow the agency’s own warnings and your company’s procedure: this map can be minutes behind them.';

/** The organisation switch of the state feeds (`tenant_service_switch`, decision 805: on for Thomson Airborne). */
export const FIRE_FEEDS_SWITCH = 'fire_feeds';
/** The organisation switch of bushfire.io (decision 807: on for Thomson Airborne). */
export const BUSHFIRE_IO_SWITCH = 'bushfireio';

/** An organisation's fire switches: the state feeds, and bushfire.io. A missing row is off. */
export interface FireSwitches {
  readonly stateFeeds: boolean;
  readonly bushfireIo: boolean;
}
export const FIRE_SWITCHES_OFF: FireSwitches = { stateFeeds: false, bushfireIo: false };

/** The switches from `tenant_service_switch` rows (`service`, `enabled`); anything else is off. */
export function readFireSwitches(rows: unknown): FireSwitches {
  const on = (service: string): boolean =>
    Array.isArray(rows) &&
    rows.some(
      (r) =>
        r !== null &&
        typeof r === 'object' &&
        (r as { service?: unknown }).service === service &&
        (r as { enabled?: unknown }).enabled === true,
    );
  return { stateFeeds: on(FIRE_FEEDS_SWITCH), bushfireIo: on(BUSHFIRE_IO_SWITCH) };
}

export const FIRE_FEEDS: Readonly<Record<FireFeedId, FireFeedDef>> = {
  'wa-warnings': {
    id: 'wa-warnings',
    state: 'WA',
    name: 'Emergency WA warnings',
    url: 'https://api.emergency.wa.gov.au/v1/capau',
    // Emergency WA's terms: "one request per data feed every 5 minutes"
    everyMs: 5 * MIN,
    credit: WA_FIRE_CREDIT,
    licence: 'CC BY-ND 4.0',
  },
  'wa-incidents': {
    id: 'wa-incidents',
    state: 'WA',
    name: 'Emergency WA incidents',
    url: 'https://api.emergency.wa.gov.au/v1/rss/incidents',
    everyMs: 5 * MIN,
    credit: WA_FIRE_CREDIT,
    licence: 'CC BY-ND 4.0',
  },
  nsw: {
    id: 'nsw',
    state: 'NSW',
    name: 'NSW RFS major incidents',
    url: 'https://www.rfs.nsw.gov.au/feeds/majorIncidents.json',
    // "Incident information is updated every 30 minutes"
    everyMs: 10 * MIN,
    credit: NSW_FIRE_CREDIT,
    licence: 'CC BY 4.0',
  },
  qld: {
    id: 'qld',
    state: 'QLD',
    name: 'Queensland Fire Department bushfire incidents',
    url: 'https://publiccontent-gis-psba-qld-gov-au.s3.amazonaws.com/content/Feeds/BushfireCurrentIncidents/bushfireAlert.json',
    // "updated every 30 minutes"
    everyMs: 10 * MIN,
    credit: QLD_FIRE_CREDIT,
    licence: 'CC BY 4.0',
  },
};

export const FIRE_FEED_IDS: readonly FireFeedId[] = ['wa-warnings', 'wa-incidents', 'nsw', 'qld'];

/** Where an incident on the map came from: a state feed, or bushfire.io (Thomson only, decision 476; §31). */
export type FireSourceId = FireFeedId | 'bushfireio';

/** A source's name, licence, credit and the site its links open, for the identify panel and the map's credits. */
export function fireSourceInfo(id: FireSourceId): {
  readonly name: string;
  readonly licence: string;
  readonly credit: string;
  readonly site: string;
} {
  if (id === 'bushfireio')
    return {
      name: 'bushfire.io (Disaster Science)',
      licence: 'shown while Braynewave asks Disaster Science for a licence',
      credit: '© Disaster Science (bushfire.io)',
      site: 'agency',
    };
  const f = FIRE_FEEDS[id];
  return { name: f.name, licence: f.licence, credit: f.credit, site: AGENCY_SITE[f.state] };
}

/** Each state's box with a degree to spare (WA's coast and islands, NSW with the ACT, Queensland with the Torres Strait). */
export const FIRE_STATE_BOUNDS: Readonly<Record<FireState, GeoBounds>> = {
  WA: { south: -36.5, west: 111.5, north: -12.5, east: 130.0 },
  NSW: { south: -38.5, west: 140.0, north: -27.5, east: 154.5 },
  QLD: { south: -30.0, west: 137.0, north: -9.0, east: 154.5 },
};

const overlaps = (a: GeoBounds, b: GeoBounds): boolean =>
  a.south <= b.north && a.north >= b.south && a.west <= b.east && a.east >= b.west;

/** The feeds to ask for what the map shows (the view and the project area): only the states it looks at. */
export function fireFeedsFor(areas: readonly (GeoBounds | null)[]): FireFeedId[] {
  const shown = areas.filter((a): a is GeoBounds => a !== null);
  return FIRE_FEED_IDS.filter((id) =>
    shown.some((a) => overlaps(a, FIRE_STATE_BOUNDS[FIRE_FEEDS[id].state])),
  );
}

/** Whether a feed may be asked again (never sooner than its gap, whatever the last answer was). */
export function fireFeedDue(id: FireFeedId, lastAskedMs: number | null, nowMs: number): boolean {
  return lastAskedMs === null || nowMs - lastAskedMs >= FIRE_FEEDS[id].everyMs;
}

// ------------------------------------------------------------------------------------------------------- the model

/** The Australian Warning System's three levels, or none (an incident with no warning). */
export type FireLevel = 'emergency_warning' | 'watch_and_act' | 'advice' | 'none';
export type FireKind = 'bushfire' | 'planned_burn' | 'fire';

/** One incident or warning as the agency published it. */
export interface FireIncident {
  /** `<feed>:<agency id>`. */
  readonly id: string;
  readonly feed: FireSourceId;
  readonly title: string;
  readonly kind: FireKind;
  readonly level: FireLevel;
  /** The agency's own words for the level ("Bushfire Advice", "Smoke Alert", "Not Applicable", "Information"). */
  readonly levelText: string;
  readonly at: LatLng | null;
  /** Warning areas or fire grounds (outer rings), as published. */
  readonly areas: readonly (readonly LatLng[])[];
  /** Label and value lines as the agency published them (status, type, location, size …). */
  readonly details: readonly IdentifyRow[];
  /** When the agency last updated it (ISO), when it says. */
  readonly updated: string | null;
  /** The agency's page for it. */
  readonly url: string | null;
  /** Emergency WA's CAD id, joining a warning to its incident. */
  readonly cad?: string;
  /** bushfire.io's "Agency ID" line: the agency's own id of the incident (dedupe with the state feeds). */
  readonly agencyId?: string;
}

export interface ParsedFireFeed {
  readonly incidents: FireIncident[];
  /** Items left out because they are not vegetation fires or planned burns (road crashes, structure fires …). */
  readonly otherIncidents: number;
  /** Why the answer could not be read, or null. */
  readonly problem: string | null;
}

/** The level in the agency's words: the Australian Warning System's names, whatever the hazard word before them. */
export function fireLevelOf(words: string): FireLevel {
  const w = words.toLowerCase();
  if (w.includes('emergency warning')) return 'emergency_warning';
  if (w.includes('watch and act') || w.includes('watch & act')) return 'watch_and_act';
  // Emergency WA issues smoke alerts at the advice level (its own name for them)
  if (/\badvice\b/.test(w) || w.includes('smoke alert')) return 'advice';
  return 'none';
}

const LEVEL_RANK: Readonly<Record<FireLevel, number>> = {
  emergency_warning: 3,
  watch_and_act: 2,
  advice: 1,
  none: 0,
};

// ----------------------------------------------------------------------------------------------- reading the feeds

const ENTITIES: Readonly<Record<string, string>> = {
  amp: '&',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  nbsp: ' ',
};

function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] === '#') {
      const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : Number(e.slice(1));
      return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    }
    return ENTITIES[e.toLowerCase()] ?? m;
  });
}

/** Text of markup: tags dropped (a line break where `<br>` was), entities read, spaces tidied. */
export function feedText(html: string): string {
  return decodeEntities(
    html
      .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, ' '),
  )
    .split('\n')
    .map((l) => l.replace(/[ \t\r\f\v]+/g, ' ').trim())
    .filter(Boolean)
    .join('\n');
}

const escapeTag = (tag: string): string => tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** The inner XML of each `<tag>` element (not nested in itself). */
function xmlBlocks(xml: string, tag: string): string[] {
  const t = escapeTag(tag);
  const re = new RegExp(`<${t}(?:\\s[^>]*)?>([\\s\\S]*?)</${t}>`, 'g');
  return [...xml.matchAll(re)].map((m) => m[1] ?? '');
}

/** The text of the first `<tag>` element, CDATA and entities read, trimmed; '' when absent or empty. */
function xmlText(xml: string, tag: string): string {
  const b = xmlBlocks(xml, tag)[0];
  if (b === undefined) return '';
  const cdata = /^\s*<!\[CDATA\[([\s\S]*?)\]\]>\s*$/.exec(b);
  return (cdata ? (cdata[1] ?? '') : decodeEntities(b)).trim();
}

const onGlobe = (lat: number, lng: number): boolean =>
  Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

function point(lat: unknown, lng: unknown): LatLng | null {
  const la = typeof lat === 'number' ? lat : Number(lat);
  const ln = typeof lng === 'number' ? lng : Number(lng);
  return typeof lat !== 'undefined' && lat !== null && lat !== '' && onGlobe(la, ln)
    ? { lat: la, lng: ln }
    : null;
}

/** A CAP polygon: "lat,lon lat,lon …" (CAP 1.2 §3.3.2: at least four pairs, first and last the same). */
function capPolygon(text: string): LatLng[] | null {
  const pts: LatLng[] = [];
  for (const pair of text.trim().split(/\s+/)) {
    const [la, ln] = pair.split(',');
    const p = point(la, ln);
    if (!p) return null;
    pts.push(p);
  }
  return pts.length >= 4 ? pts : null;
}

function isoOrNull(s: string): string | null {
  const t = Date.parse(s);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

const row = (label: string, value: string | null | undefined): IdentifyRow[] =>
  value && value.trim() ? [{ label, value: value.trim() }] : [];

/** Emergency WA's warnings (CAP-AU in an EDXL-DE envelope): fire and smoke alerts with their areas. */
export function parseEmergencyWaCap(xml: string): ParsedFireFeed {
  if (!/<cap:alert[\s>]/.test(xml) && !/EDXLDistribution/.test(xml))
    return { incidents: [], otherIncidents: 0, problem: 'Emergency WA did not send its CAP-AU warnings.' };
  const incidents: FireIncident[] = [];
  let other = 0;
  for (const alert of xmlBlocks(xml, 'cap:alert')) {
    const id = xmlText(alert, 'cap:identifier');
    const status = xmlText(alert, 'cap:status');
    const msgType = xmlText(alert, 'cap:msgType');
    if (status !== 'Actual' || msgType === 'Cancel') continue;
    for (const [n, info] of xmlBlocks(alert, 'cap:info').entries()) {
      // fire and smoke only: a park closure ("Infra") is not a fire
      if (xmlText(info, 'cap:category') !== 'Fire') {
        other += 1;
        continue;
      }
      const params = new Map(
        xmlBlocks(info, 'cap:parameter').map((p) => [xmlText(p, 'cap:valueName'), xmlText(p, 'cap:value')]),
      );
      const areaBlocks = xmlBlocks(info, 'cap:area');
      const levelText =
        areaBlocks.map((a) => xmlText(a, 'cap:alertLevel')).find(Boolean) ?? xmlText(info, 'cap:event');
      const areas = areaBlocks
        .flatMap((a) => xmlBlocks(a, 'cap:polygon'))
        .map(capPolygon)
        .filter((p): p is LatLng[] => p !== null);
      const circle = areaBlocks.map((a) => xmlText(a, 'cap:circle')).find(Boolean) ?? '';
      const [centre] = circle.split(/\s+/);
      const at = centre ? point(...(centre.split(',') as [string, string])) : null;
      const event = xmlText(info, 'cap:event');
      incidents.push({
        id: `wa-warnings:${id}${n > 0 ? `.${n}` : ''}`,
        feed: 'wa-warnings',
        title: xmlText(info, 'cap:headline') || levelText,
        kind: /burn/i.test(event) ? 'planned_burn' : 'bushfire',
        level: fireLevelOf(levelText),
        levelText,
        at,
        areas,
        details: [
          ...row('Warning', levelText),
          ...row('Event', event),
          ...row('Area', xmlText(areaBlocks[0] ?? '', 'cap:areaDesc')),
          ...row('What to do', xmlText(info, 'cap:instruction')),
          ...row('Region', params.get('DFES_Region')),
          ...row('Agency', params.get('Agency_Responsible') ?? xmlText(info, 'cap:senderName')),
          ...row('CAD id', params.get('CAD_ID')),
        ],
        updated: isoOrNull(xmlText(alert, 'cap:sent')),
        url: xmlText(info, 'cap:web') || null,
        ...(params.get('CAD_ID') ? { cad: params.get('CAD_ID')! } : {}),
      });
    }
  }
  return { incidents, otherIncidents: other, problem: null };
}

/** The incident types of Emergency WA's RSS that are shown: bushfires, other fires and burn-offs. */
function waKind(type: string): FireKind | null {
  const t = type.toLowerCase();
  if (t === 'bushfire') return 'bushfire';
  if (t === 'burn off') return 'planned_burn';
  if (t === 'fire') return 'fire';
  return null; // structure fire, vehicle fire, road crash, active alarm …
}

/** Emergency WA's incidents (RSS 2.0 with georss and dfes elements): points. */
export function parseEmergencyWaIncidents(xml: string): ParsedFireFeed {
  if (!/<rss[\s>]/.test(xml))
    return { incidents: [], otherIncidents: 0, problem: 'Emergency WA did not send its incident list.' };
  const incidents: FireIncident[] = [];
  let other = 0;
  for (const item of xmlBlocks(xml, 'item')) {
    const title = feedText(xmlText(item, 'title'));
    // "Bushfire (ORD RIVER, SHIRE OF HALLS CREEK, KIMBERLEY, CAD-ID: 823443)"
    const m = /^(.*?)\s*\((.*)\)\s*$/.exec(title);
    const type = (m?.[1] ?? title).trim();
    const kind = waKind(type);
    if (!kind) {
      other += 1;
      continue;
    }
    const inside = (m?.[2] ?? '').split(',').map((s) => s.trim());
    const cadPart = inside.find((s) => /^CAD-ID:/i.test(s));
    const cad = cadPart?.replace(/^CAD-ID:\s*/i, '') ?? '';
    const places = inside.filter((s) => s !== cadPart);
    const area = xmlText(item, 'georss:polygon');
    incidents.push({
      id: `wa-incidents:${xmlText(item, 'guid') || cad || title}`,
      feed: 'wa-incidents',
      title,
      kind,
      level: 'none',
      levelText: '',
      at: point(xmlText(item, 'geo:lat'), xmlText(item, 'geo:long')),
      areas: area ? [capPolygonGeoRss(area)].filter((p): p is LatLng[] => p !== null) : [],
      details: [
        ...row('Type', type),
        ...row('Location', places.join(', ')),
        ...row('CAD id', cad),
        ...row('Published', xmlText(item, 'pubDate')),
      ],
      updated: isoOrNull(xmlText(item, 'pubDate')),
      url: xmlText(item, 'link') || null,
      ...(cad ? { cad } : {}),
    });
  }
  return { incidents, otherIncidents: other, problem: null };
}

/** A GeoRSS polygon: "lat lon lat lon …". */
function capPolygonGeoRss(text: string): LatLng[] | null {
  const n = text.trim().split(/\s+/).map(Number);
  if (n.length < 8 || n.length % 2 !== 0) return null;
  const pts: LatLng[] = [];
  for (let i = 0; i < n.length; i += 2) {
    const p = point(n[i], n[i + 1]);
    if (!p) return null;
    pts.push(p);
  }
  return pts;
}

type Json = Record<string, unknown>;
const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string =>
  typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '';

/** GeoJSON geometry to points and outer rings (GeometryCollections nested, as the RFS feed sends them). */
function geoJsonParts(g: unknown): { points: LatLng[]; rings: LatLng[][] } {
  const out = { points: [] as LatLng[], rings: [] as LatLng[][] };
  const ring = (r: unknown): LatLng[] | null => {
    if (!Array.isArray(r)) return null;
    const pts = r.map((c) => (Array.isArray(c) ? point(c[1], c[0]) : null));
    return pts.length >= 4 && pts.every((p) => p !== null) ? (pts as LatLng[]) : null;
  };
  const walk = (x: unknown, depth: number): void => {
    if (!isObj(x) || depth > 4) return;
    const c = x.coordinates;
    if (x.type === 'GeometryCollection' && Array.isArray(x.geometries))
      for (const y of x.geometries) walk(y, depth + 1);
    else if (x.type === 'Point' && Array.isArray(c)) {
      const p = point(c[1], c[0]);
      if (p) out.points.push(p);
    } else if (x.type === 'Polygon' && Array.isArray(c)) {
      const r = ring(c[0]);
      if (r) out.rings.push(r);
    } else if (x.type === 'MultiPolygon' && Array.isArray(c)) {
      for (const poly of c) {
        const r = Array.isArray(poly) ? ring(poly[0]) : null;
        if (r) out.rings.push(r);
      }
    }
  };
  walk(g, 0);
  return out;
}

/** "ALERT LEVEL: Planned Burn <br />LOCATION: …" as label and value pairs, in order. */
function rfsFields(description: string): [string, string][] {
  return feedText(description)
    .split('\n')
    .map((l) => /^([A-Z][A-Z /]+):\s*(.*)$/.exec(l))
    .filter((m): m is RegExpExecArray => m !== null)
    .map((m) => [m[1]!.trim(), m[2]!.trim()]);
}

const sentence = (label: string): string => label.charAt(0) + label.slice(1).toLowerCase();

/** The RFS's "21/10/2026 1:07:00 AM" (UTC). */
function rfsDate(s: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) ([AP]M)$/.exec(s.trim());
  if (!m) return null;
  let h = Number(m[4]) % 12;
  if (m[7] === 'PM') h += 12;
  const t = Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), h, Number(m[5]), Number(m[6]));
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

/** NSW RFS major incidents (GeoJSON): the point and the fire ground of each incident. */
export function parseNswMajorIncidents(text: string): ParsedFireFeed {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return { incidents: [], otherIncidents: 0, problem: 'The RFS feed was not readable.' };
  }
  if (!isObj(doc) || !Array.isArray(doc.features))
    return { incidents: [], otherIncidents: 0, problem: 'The RFS feed was not a list of incidents.' };
  const incidents: FireIncident[] = [];
  let other = 0;
  for (const f of doc.features) {
    if (!isObj(f) || !isObj(f.properties)) continue;
    const p = f.properties;
    const fields = rfsFields(str(p.description));
    const get = (k: string): string => fields.find(([l]) => l === k)?.[1] ?? '';
    const category = str(p.category);
    const level = fireLevelOf(category);
    const type = get('TYPE');
    const kind: FireKind | null =
      /planned burn/i.test(category) || /hazard reduction|pile burn|burn off/i.test(type)
        ? 'planned_burn'
        : /bush|grass|scrub|forest|vegetation/i.test(type)
          ? 'bushfire'
          : level !== 'none'
            ? 'fire' // a warning always shows, whatever the type
            : null;
    if (!kind) {
      other += 1;
      continue;
    }
    const { points, rings } = geoJsonParts(f.geometry);
    const guid = str(p.guid);
    incidents.push({
      id: `nsw:${guid.split('/').pop() || str(p.title)}`,
      feed: 'nsw',
      title: str(p.title),
      kind,
      level,
      levelText: category,
      at: points[0] ?? null,
      areas: rings,
      details: fields.map(([l, v]) => ({ label: sentence(l), value: v })).filter((r) => r.value),
      updated: rfsDate(str(p.pubDate)),
      url: str(p.link) || null,
    });
  }
  return { incidents, otherIncidents: other, problem: null };
}

/** Queensland Fire Department's current bushfire incidents and warnings (GeoJSON). */
export function parseQldBushfireAlert(text: string): ParsedFireFeed {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return { incidents: [], otherIncidents: 0, problem: 'The Queensland feed was not readable.' };
  }
  if (!isObj(doc) || !Array.isArray(doc.features))
    return { incidents: [], otherIncidents: 0, problem: 'The Queensland feed was not a list of incidents.' };
  const incidents: FireIncident[] = [];
  let other = 0;
  for (const f of doc.features) {
    if (!isObj(f) || !isObj(f.properties)) continue;
    const p = f.properties;
    const levelText = str(p.WarningLevel);
    const level = fireLevelOf(levelText);
    const grouped = str(p.GroupedType);
    const kind: FireKind | null = /permitted burn/i.test(grouped)
      ? 'planned_burn'
      : /vegetation/i.test(grouped)
        ? 'bushfire'
        : level !== 'none'
          ? 'fire'
          : null;
    if (!kind || str(p.EventType).toLowerCase() !== 'fire') {
      other += 1;
      continue;
    }
    const { points, rings } = geoJsonParts(f.geometry);
    incidents.push({
      id: `qld:${str(p.UniqueID) || str(p.OBJECTID)}`,
      feed: 'qld',
      title: str(p.WarningTitle),
      kind,
      level,
      levelText,
      at: points[0] ?? point(p.Latitude, p.Longitude),
      areas: rings,
      details: [
        ...row('Warning level', levelText),
        ...row('Status', str(p.CurrentStatus)),
        ...row('Type', grouped),
        ...row('Location', [str(p.Location), str(p.Locality)].filter(Boolean).join(', ')),
        ...row('Area', str(p.WarningArea)),
        ...row('What it says', str(p.Header)),
        ...row('Call to action', str(p.CallToAction) !== str(p.Header) ? str(p.CallToAction) : ''),
        ...row('Region', str(p.Jurisdiction)),
      ],
      updated: isoOrNull(str(p.ItemDateTimeLocal_ISO)) ?? isoOrNull(str(p.PublishDateLocal_ISO)),
      url: 'https://www.fire.qld.gov.au/current-incidents',
    });
  }
  return { incidents, otherIncidents: other, problem: null };
}

/** Reads one feed's answer. */
export function parseFireFeed(id: FireFeedId, body: string): ParsedFireFeed {
  switch (id) {
    case 'wa-warnings':
      return parseEmergencyWaCap(body);
    case 'wa-incidents':
      return parseEmergencyWaIncidents(body);
    case 'nsw':
      return parseNswMajorIncidents(body);
    case 'qld':
      return parseQldBushfireAlert(body);
  }
}

/**
 * Emergency WA's warnings join their incident by CAD id: the incident point takes the warning's level, words and
 * link, and the warning's area; a warning whose incident is not listed stays on its own (at its circle's centre).
 */
export function joinWaWarnings(incidents: readonly FireIncident[]): FireIncident[] {
  const warnings = incidents.filter((i) => i.feed === 'wa-warnings');
  const byCad = new Map<string, FireIncident[]>();
  for (const w of warnings) if (w.cad) byCad.set(w.cad, [...(byCad.get(w.cad) ?? []), w]);
  const joined = new Set<string>();
  const out: FireIncident[] = [];
  for (const i of incidents) {
    if (i.feed === 'wa-warnings') continue;
    const ws = i.feed === 'wa-incidents' && i.cad ? (byCad.get(i.cad) ?? []) : [];
    if (ws.length === 0) {
      out.push(i);
      continue;
    }
    const top = [...ws].sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level])[0]!;
    for (const w of ws) joined.add(w.id);
    out.push({
      ...i,
      level: top.level,
      levelText: top.levelText,
      title: top.title,
      areas: [...i.areas, ...ws.flatMap((w) => w.areas)],
      details: [...top.details, ...i.details.filter((d) => d.label !== 'CAD id')],
      url: top.url ?? i.url,
      updated: top.updated ?? i.updated,
    });
  }
  return [...out, ...warnings.filter((w) => !joined.has(w.id))];
}

// ------------------------------------------------------------------------------------------------------- the layer

/** Fire colours (`brand/tokens/tokens.json` group `fire`): the Australian Warning System's, the same in both themes. */
export type FireColour =
  'fire-emergency-warning' | 'fire-watch-and-act' | 'fire-advice' | 'fire-no-warning' | 'fire-planned-burn';

export const FIRE_TOKENS: readonly FireColour[] = [
  'fire-emergency-warning',
  'fire-watch-and-act',
  'fire-advice',
  'fire-no-warning',
  'fire-planned-burn',
];

interface FireLook {
  readonly colour: FireColour;
  readonly word: string;
}

/** The colour and word of an item: its warning level; a planned burn without a warning is green. */
export function fireLook(i: Pick<FireIncident, 'level' | 'kind'>): FireLook {
  switch (i.level) {
    case 'emergency_warning':
      return { colour: 'fire-emergency-warning', word: 'Emergency Warning' };
    case 'watch_and_act':
      return { colour: 'fire-watch-and-act', word: 'Watch and Act' };
    case 'advice':
      return { colour: 'fire-advice', word: 'Advice' };
    case 'none':
      return i.kind === 'planned_burn'
        ? { colour: 'fire-planned-burn', word: 'Planned burn, no warning' }
        : { colour: 'fire-no-warning', word: 'No warning' };
  }
}

/** A fire legend row: a flame in the look's colour, dashed when there is no warning (as `fireFeatures` draws it). */
const fireLegendRow = (l: FireLook): LegendEntry => ({
  colour: l.colour,
  stroke: 'normal',
  dashed: l.colour === 'fire-no-warning' || l.colour === 'fire-planned-burn',
  word: l.word,
  symbol: 'fire',
});

/** Every warning level the layer can draw, in `FIRE_TOKENS` order: the map legend's "Show all" (docs/specs/map.md §35.8). */
export const FIRE_LEGEND_ALL: readonly LegendEntry[] = [
  fireLook({ level: 'emergency_warning', kind: 'bushfire' }),
  fireLook({ level: 'watch_and_act', kind: 'bushfire' }),
  fireLook({ level: 'advice', kind: 'bushfire' }),
  fireLook({ level: 'none', kind: 'bushfire' }),
  fireLook({ level: 'none', kind: 'planned_burn' }),
].map(fireLegendRow);

const KIND_WORD: Readonly<Record<FireKind, string>> = {
  bushfire: 'Bushfire',
  planned_burn: 'Planned burn',
  fire: 'Fire',
};

const AGENCY_SITE: Readonly<Record<FireState, string>> = {
  WA: 'Emergency WA',
  NSW: 'NSW RFS',
  QLD: 'Queensland Fire Department',
};

export interface FireLayerOptions {
  /** Show planned burns without a warning (off at first, as MB's own app). */
  readonly plannedBurns: boolean;
}

export interface FireLayer {
  readonly features: MapFeature[];
  readonly notDrawn: string[];
  readonly legend: LegendEntry[];
  /** Items drawn, by level. */
  readonly counts: Readonly<Record<FireLevel, number>>;
  readonly plannedBurnsHidden: number;
  /** The credits of the agencies whose items are drawn, for the map's attribution. */
  readonly credits: string[];
}

const centroid = (ring: readonly LatLng[]): LatLng => {
  const pts = ring.length > 1 ? ring.slice(0, -1) : ring;
  return {
    lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
    lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length,
  };
};

/**
 * The fire layer: each warning area or fire ground as a polygon in its level's colour, and a marker at each incident
 * (the published point, else the first area's centre). Warnings first so markers draw on top; the highest level
 * last of all. The identify rows are the agency's own lines, with the source and its credit.
 */
export function fireFeatures(incidents: readonly FireIncident[], opts: FireLayerOptions): FireLayer {
  const counts: Record<FireLevel, number> = { emergency_warning: 0, watch_and_act: 0, advice: 0, none: 0 };
  const polygons: MapFeature[] = [];
  const markers: MapFeature[] = [];
  const notDrawn: string[] = [];
  const looks = new Map<FireColour, FireLook>();
  const credits = new Set<string>();
  let burnsHidden = 0;
  const ordered = [...incidents].sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
  for (const i of ordered) {
    if (i.kind === 'planned_burn' && i.level === 'none' && !opts.plannedBurns) {
      burnsHidden += 1;
      continue;
    }
    const at = i.at ?? (i.areas[0] ? centroid(i.areas[0]) : null);
    if (!at) {
      notDrawn.push(`${i.title}: no position in the feed`);
      continue;
    }
    const look = fireLook(i);
    looks.set(look.colour, look);
    credits.add(fireSourceInfo(i.feed).credit);
    counts[i.level] += 1;
    const levelWords = i.levelText && fireLevelOf(i.levelText) !== 'none' ? i.levelText : look.word;
    const tooltip = `${i.title} · ${levelWords}`;
    const feed = fireSourceInfo(i.feed);
    const identify: IdentifyRow[] = [
      { label: KIND_WORD[i.kind], value: i.title },
      { label: 'Warning level', value: levelWords },
      ...i.details.filter((d) => d.label !== 'Warning' && d.label !== 'Warning level'),
      ...(i.updated ? [{ label: 'Updated (UTC)', value: i.updated.slice(0, 16).replace('T', ' ') }] : []),
      { label: 'Source', value: `${feed.name} (${feed.licence})` },
      { label: 'Credit', value: feed.credit },
      { label: 'Note', value: FIRE_NOTICE },
    ];
    const link = i.url ? { url: i.url, label: `Open on the ${feed.site} site` } : undefined;
    i.areas.forEach((ring, n) => {
      const style: FeatureStyle = {
        colour: look.colour,
        stroke: i.level === 'emergency_warning' ? 'thick' : 'normal',
        dashed: i.level === 'none',
        fill: i.level === 'none' ? 0.15 : 0.25,
      };
      polygons.push({
        id: `${i.id}#${n}`,
        layer: 'fires',
        geometry: { type: 'polygon', rings: [ring] },
        style,
        tooltip,
        identify,
        ...(link ? { link } : {}),
      });
    });
    markers.push({
      id: i.id,
      layer: 'fires',
      geometry: { type: 'point', at },
      // a flame in the warning's colour (R143)
      style: { colour: look.colour, stroke: 'normal', dashed: false, fill: 0.9, symbol: 'fire' },
      tooltip,
      identify,
      ...(link ? { link } : {}),
    });
  }
  const legend: LegendEntry[] = FIRE_TOKENS.filter((c) => looks.has(c)).map((c) =>
    fireLegendRow(looks.get(c)!),
  );
  return {
    features: [...polygons, ...markers],
    notDrawn,
    legend,
    counts,
    plannedBurnsHidden: burnsHidden,
    credits: [...credits],
  };
}

// --------------------------------------------------------------------------------------------- the laptop's copy

/** The last answer of each feed, kept on the laptop so fires show offline and after a restart, with their age. */
export interface FireFeedCopy {
  readonly feed: FireFeedId;
  readonly body: string;
  /** When the feed answered (ISO). */
  readonly fetchedAt: string;
}

/** A copy older than this is not drawn: the fires it shows may be long out. */
export const FIRE_COPY_MAX_AGE_MS = 12 * 60 * MIN;

export function readFireFeedCopy(raw: unknown): FireFeedCopy | null {
  if (!isObj(raw)) return null;
  const feed = raw.feed;
  if (typeof feed !== 'string' || !(FIRE_FEED_IDS as readonly string[]).includes(feed)) return null;
  if (
    typeof raw.body !== 'string' ||
    typeof raw.fetchedAt !== 'string' ||
    !Number.isFinite(Date.parse(raw.fetchedAt))
  )
    return null;
  return { feed: feed as FireFeedId, body: raw.body, fetchedAt: raw.fetchedAt };
}

/** Every incident of the copies young enough to draw, Emergency WA's warnings joined to their incidents. */
export function fireIncidentsOf(
  copies: readonly FireFeedCopy[],
  nowMs: number,
): { incidents: FireIncident[]; problems: string[]; tooOld: FireFeedId[]; otherIncidents: number } {
  const all: FireIncident[] = [];
  const problems: string[] = [];
  const tooOld: FireFeedId[] = [];
  let other = 0;
  for (const c of copies) {
    if (nowMs - Date.parse(c.fetchedAt) > FIRE_COPY_MAX_AGE_MS) {
      tooOld.push(c.feed);
      continue;
    }
    const parsed = parseFireFeed(c.feed, c.body);
    if (parsed.problem) problems.push(parsed.problem);
    other += parsed.otherIncidents;
    all.push(...parsed.incidents);
  }
  return { incidents: joinWaWarnings(all), problems, tooOld, otherIncidents: other };
}
